import React, { useMemo, useState } from 'react'
import { Alert, Box, Skeleton, Typography } from '@mui/material'
import { useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { restaurantDashboard } from '../apollo'
import { C, FONT, cssVars, tabular } from '../components/RestaurantDashboard/tokens'
import {
  DEFAULT_TZ, addDays, compareLabel, count, dayShort, downloadCsv, longToday, money, spanDays, timeIn, todayIn
} from '../components/RestaurantDashboard/format'
import FilterBar, { PRESETS } from '../components/RestaurantDashboard/FilterBar'
import KpiCard from '../components/RestaurantDashboard/KpiCard'
import SalesOverTime from '../components/RestaurantDashboard/SalesOverTime'
import WeekdayHour from '../components/RestaurantDashboard/WeekdayHour'
import SalesBreakdown from '../components/RestaurantDashboard/SalesBreakdown'
import { RatingsSnapshot, RecentOrders, TopItems } from '../components/RestaurantDashboard/Widgets'
import Insight, { insightFor } from '../components/RestaurantDashboard/Insight'

/**
 * The restaurant Dashboard.
 *
 * Everything on it comes from ONE query, restaurantDashboard, which counts all
 * three order sources (marketplace/POS, partner email orders, old RushMyFood
 * orders) in the restaurant's own timezone and excludes cancelled and unpaid
 * orders. The page only chooses the range, comparison and channel and draws.
 */
const RESTAURANT_DASHBOARD = gql`${restaurantDashboard}`

const rangeFor = (key, today) => {
  const p = PRESETS.find(x => x.key === key)
  return { from: addDays(today, -((p ? p.days : 7) - 1)), to: today }
}

function StatusPill({ open }) {
  return (
    <Box
      role="status"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '999px', border: `1px solid ${C.border}`, bgcolor: '#fff' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: open ? C.open : C.closed }} aria-hidden />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{open ? 'Open' : 'Closed'}</Typography>
    </Box>
  )
}

const Dashboard = () => {
  const restaurantId = localStorage.getItem('restaurantId')
  const storedName = localStorage.getItem('restaurantName')

  const today = todayIn(DEFAULT_TZ)
  // Opened from the super admin leaderboard: start on the range it was showing.
  const [handoff] = useState(() => {
    try {
      const h = JSON.parse(sessionStorage.getItem('rmf.dashRangeHandoff') || 'null')
      sessionStorage.removeItem('rmf.dashRangeHandoff')
      return h && /^\d{4}-\d{2}-\d{2}$/.test(h.from) && /^\d{4}-\d{2}-\d{2}$/.test(h.to) && spanDays(h.from, h.to) <= 366 ? h : null
    } catch (_) { return null }
  })
  const [preset, setPreset] = useState(handoff ? 'custom' : '7')
  const [range, setRange] = useState(() => (handoff ? { from: handoff.from, to: handoff.to } : rangeFor('7', today)))
  const [compare, setCompare] = useState(handoff && ['previous', 'year', 'none'].includes(handoff.compare) ? handoff.compare : 'previous')
  const [channel, setChannel] = useState('ALL')
  const [insightHidden, setInsightHidden] = useState(false)

  const { data: fresh, previousData, loading, error } = useQuery(RESTAURANT_DASHBOARD, {
    variables: { restaurantId, from: range.from, to: range.to, compare, channel },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })
  // Keep the last result on screen while the next one loads — no blank flash,
  // no layout shift. Skeletons show only on the very first load.
  const data = (fresh || previousData)?.restaurantDashboard
  const tz = data?.timezone || DEFAULT_TZ
  const cmpText = compareLabel(compare, range.from, range.to)

  const spark = useMemo(() => {
    const days = data?.days || []
    const gaps = days.map(d => d.closed)
    return {
      gaps,
      sales: days.map(d => d.sales),
      orders: days.map(d => d.orders),
      aov: days.map(d => (d.orders ? d.sales / d.orders : 0)),
      // A day with no orders has no completion rate — a gap, not 0%.
      completion: days.map(d => {
        const all = d.orders + (d.cancelledOrders || 0)
        return all ? (d.orders / all) * 100 : null
      })
    }
  }, [data])

  const onPreset = key => {
    setPreset(key)
    setRange(rangeFor(key, today))
  }
  const onRange = (from, to) => {
    setPreset('custom')
    setRange({ from, to })
  }

  const exportCsv = () => {
    if (!data) return
    const slices = data.breakdown.channels
    const rows = [
      ['Date', 'Weekday', 'Gross sales', 'Orders', 'Avg order value', 'Online orders',
        ...(data.previous ? ['Comparison date', 'Comparison sales', 'Comparison orders'] : []),
        ...slices.map(s => `${s.label} sales`)],
      ...data.days.map((d, i) => [
        d.date, d.weekday, d.sales.toFixed(2), d.orders, d.orders ? (d.sales / d.orders).toFixed(2) : '0.00', d.onlineOrders,
        ...(data.previous ? [d.prevDate, d.prevSales == null ? '' : d.prevSales.toFixed(2), d.prevOrders ?? ''] : []),
        ...slices.map(s => s.daily[i].toFixed(2))
      ]),
      [],
      ['Total', '', data.current.grossSales.toFixed(2), data.current.orders, data.current.avgOrderValue.toFixed(2), data.current.onlineOrders,
        ...(data.previous ? ['', data.previous.grossSales.toFixed(2), data.previous.orders] : [])]
    ]
    const slug = (data.restaurantName || 'restaurant').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    downloadCsv(`${slug}-sales-${range.from}-to-${range.to}.csv`, rows)
  }

  const first = loading && !data
  const empty = data && data.current.orders === 0
  const cur = data?.current
  const prev = data?.previous
  // Completion rate = orders that went through ÷ all orders placed (cancelled
  // ones included). Unpaid online checkouts never counted as placed.
  const rate = p => (p && p.orders + p.cancelledOrders > 0 ? (p.orders / (p.orders + p.cancelledOrders)) * 100 : null)
  const curRate = rate(cur)
  const prevRate = rate(prev)
  const pctText = v => (v == null ? '—' : `${v >= 99.95 ? 100 : v.toFixed(1)}%`)
  const ptsText = v => `${Math.abs(v).toFixed(1)} pts`
  const insight = insightHidden ? null : insightFor(data, compare)

  return (
    <Box sx={{ ...cssVars, bgcolor: 'var(--page)', minHeight: '100vh', fontFamily: FONT, color: C.text, ...tabular }}>
      {/* Header — on the page background, no banner. */}
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 4 }, pt: { xs: 2, md: 4 }, pb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2, color: C.text }}>Dashboard</Typography>
          <Typography sx={{ fontSize: 14, color: C.textMuted, mt: 0.5 }}>
            {(data?.restaurantName || storedName || 'Your restaurant')} · {longToday(tz)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 32 }}>
          {data ? <StatusPill open={data.isOpen} /> : <Skeleton variant="rounded" width={78} height={30} sx={{ borderRadius: '999px' }} />}
          <Typography sx={{ fontSize: 13, color: C.textMuted, ...tabular }} aria-live="polite">
            {loading && data ? 'Updating…' : data ? `Last updated ${timeIn(data.generatedAt, tz)}` : ' '}
          </Typography>
        </Box>
      </Box>

      <FilterBar
        preset={preset}
        from={range.from}
        to={range.to}
        today={today}
        compare={compare}
        channel={channel}
        onPreset={onPreset}
        onRange={onRange}
        onCompare={setCompare}
        onChannel={setChannel}
        onExport={exportCsv}
        exportDisabled={!data}
      />

      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 4 }, pb: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error ? (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>
            The dashboard could not load: {error.message}
          </Alert>
        ) : null}

        <Insight text={insight} onDismiss={() => setInsightHidden(true)} />

        {/* KPIs — 4 across, 2×2 on tablet, 1 column on phones. */}
        <Box component="section" aria-label="Key figures" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
          <KpiCard label="Gross sales" hint="Items subtotal before tax, delivery charges, service fees and tips. Cancelled and unpaid orders are not counted." value={money(cur?.grossSales)} cur={cur?.grossSales} prev={prev?.grossSales} prevDisplay={money(prev?.grossSales)} fmtAbs={money}
            compareText={prev ? cmpText : ''} spark={spark.sales} gaps={spark.gaps} loading={first} empty={empty} />
          <KpiCard label="Total orders" value={count(cur?.orders)} cur={cur?.orders} prev={prev?.orders} prevDisplay={count(prev?.orders)} fmtAbs={count}
            compareText={prev ? cmpText : ''} spark={spark.orders} gaps={spark.gaps} loading={first} empty={empty} />
          <KpiCard label="Avg order value" value={money(cur?.avgOrderValue)} cur={cur?.avgOrderValue} prev={prev?.avgOrderValue} prevDisplay={money(prev?.avgOrderValue)} fmtAbs={money}
            compareText={prev ? cmpText : ''} spark={spark.aov} gaps={spark.gaps} loading={first} empty={empty} />
          <KpiCard label="Completion rate" hint="Orders that went through, out of all orders placed. Cancelled orders pull it down." value={pctText(curRate)} cur={curRate} prev={prevRate} prevDisplay={pctText(prevRate)} fmtAbs={ptsText} points
            compareText={prev && prevRate != null ? cmpText : ''} spark={spark.completion} gaps={spark.gaps} loading={first} empty={empty} />
        </Box>

        <SalesOverTime data={data} loading={loading} compareText={cmpText} onTry30={() => onPreset('30')} />

        <WeekdayHour data={data} loading={loading} />

        <SalesBreakdown data={data} loading={loading} />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
          <TopItems data={data} loading={loading} />
          <RecentOrders data={data} loading={loading} tz={tz} />
          <RatingsSnapshot data={data} loading={loading} tz={tz} />
        </Box>

        {cur && cur.cancelledOrders > 0 ? (
          <Typography sx={{ fontSize: 12, color: C.textMuted }}>
            {count(cur.cancelledOrders)} cancelled order{cur.cancelledOrders === 1 ? '' : 's'} between {dayShort(cur.from)} and {dayShort(cur.to)} {cur.cancelledOrders === 1 ? 'is' : 'are'} not counted in sales.
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

export default withTranslation()(Dashboard)
